import type { RefObject } from 'react'

/**
 * What a campaign body may say about its recipient.
 *
 * One list, because four screens offer it — the new-campaign form, the
 * composer, the template editor and Compose's formatting bar — and copies of
 * the sentence had already drifted into three different wordings. A person told
 * about `{{company}}` on one screen and not another cannot tell whether it
 * works.
 *
 * These are always available: `name` and `email` come from the recipient
 * itself, `company` from the contact's own column, and `unsubscribe_url` from
 * the send. A field the organisation declared is usable the same way and is
 * listed on the Fields screen beside its token, rather than repeated here —
 * this list is what every tenant has.
 *
 * `unsubscribe_url` is offered even though every message already carries an
 * unsubscribe link in its footer, for the same reason the Unsubscribe block
 * exists: a design that wants one somewhere else as well has to be able to put
 * it there, and it has to be this placeholder rather than a url somebody typed,
 * because the real address is different for every recipient and is only known
 * at send.
 *
 * An unknown placeholder renders as nothing rather than as itself, so offering
 * one that is not filled would silently delete a word from somebody's sentence.
 */

export interface MergeToken {
  /** Written into the body exactly as it appears here. */
  readonly token: string
  /** What it becomes, said in the words the contacts screen uses. */
  readonly means: string
}

export const MERGE_TOKENS: readonly MergeToken[] = [
  { token: '{{name}}', means: "the contact's name" },
  { token: '{{email}}', means: 'their email address' },
  { token: '{{company}}', means: 'their company, if you have one' },
  { token: '{{unsubscribe_url}}', means: "this recipient's own unsubscribe link" },
]

/**
 * The sentence under a message field.
 *
 * Says the two things somebody needs before typing: what the format is, and
 * that the unsubscribe link is not theirs to remember. The tokens themselves
 * are offered as buttons rather than listed here, so this stays short.
 */
export const BODY_HINT =
  'Plain text. A blank line starts a new paragraph, and an unsubscribe link is added to every message.'

/**
 * Buttons that put a placeholder where the cursor is.
 *
 * Typing `{{compnay}}` produces a message with a word missing and no warning —
 * an unknown placeholder renders as nothing rather than as itself, which is the
 * right behaviour at send and an unforgiving one while writing. A button cannot
 * be misspelled.
 *
 * It inserts into the field rather than appending to the end, because the
 * placeholder almost always belongs mid-sentence: "Hello {{name}}," is the
 * whole point of the feature.
 *
 * The textarea is passed by ref rather than the component owning the text. The
 * value lives in the view model, as everything else on these screens does, and
 * a component that held its own copy would be a second source of the body.
 */
export function MergeTokens({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: RefObject<HTMLTextAreaElement | null>
  readonly value: string
  readonly disabled: boolean
  readonly onChange: (value: string) => void
}) {
  function insert(token: string) {
    const element = field.current
    // Without the element there is no cursor to insert at, and appending to the
    // end would put the placeholder somewhere nobody asked for.
    if (element === null) return

    const start = element.selectionStart
    const end = element.selectionEnd
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`)

    // Put the caret after what was inserted, on the next frame — React has to
    // have written the new value before the position means anything.
    requestAnimationFrame(() => {
      element.focus()
      element.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="text-[12px] text-t3">Insert</span>
      {MERGE_TOKENS.map((merge) => (
        <button
          key={merge.token}
          type="button"
          disabled={disabled}
          title={merge.means}
          onClick={() => insert(merge.token)}
          className="rounded-pill border border-line px-2.5 py-1 font-mono text-[11px] text-t2 hover:bg-line-2 hover:text-t1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {merge.token}
        </button>
      ))}
    </div>
  )
}
